// Pure Comparison chart layer (04i-a) — axis domains, scales, ticks, the dot-size
// scale, the colour ramp, and the magnetic hit-test, ported verbatim from
// js/renderers-comparison.js. Kept pure (no DOM, no d3) so the correctness-critical chart
// math is unit-tested; the scatter itself (04i-b) is plain React JSX over these plus a thin
// imperative hover/drag effect. The population compute (points, season codes, metric picker)
// and COMPARISON_RANGE_FILTERS land with their consumers in 04i-b / 04i-c.

export interface CmpDomain {
  min: number;
  max: number;
}

/** null = flat default colour · "position" = categorical by template · "metric" = RdYlGn ramp. */
export type CmpColorMode = "position" | "metric" | null;

/** Shared value formatter — renderers-comparison.js:52. Used for tooltips and table cells
 *  (tick labels use the coarser {@link tickText}). */
export function formatMetricValue(raw: number | null | undefined, outputType?: string): string {
  if (raw === undefined || raw === null || Number.isNaN(raw)) return "—";
  if (outputType === "percentage") return (raw * 100).toFixed(1) + "%";
  if (outputType === "integer") return Math.round(raw).toLocaleString();
  return Math.abs(raw) >= 10 ? raw.toFixed(1) : raw.toFixed(2);
}

// --- axis domains + scales ------------------------------------------------

/** A padded axis domain. The 0-clamp is a real fix (renderers-comparison.js:238): 8% padding
 *  on both sides could invent a negative floor for a metric that can never go negative (any
 *  per-90 count) — so we clamp back to 0 only when the unpadded min was already >= 0. A metric
 *  that genuinely goes negative (OBV, xG uplift) still pads normally, since a negative floor is
 *  honest there. renderers-comparison.js:248. */
export function niceDomain(values: number[]): CmpDomain {
  if (!values.length) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return { min: min - 1, max: max + 1 };
  const pad = (max - min) * 0.08;
  let paddedMin = min - pad;
  if (min >= 0) paddedMin = Math.max(paddedMin, 0);
  return { min: paddedMin, max: max + pad };
}

/** The colour key's range on real data (Ben, 2026-10-16, Q-016): exactly lowest → highest, no
 *  8% padding (a pass % key read "0% … 108%"). One value: ±1, as `niceDomain`. */
export function exactDomain(values: number[]): CmpDomain {
  if (!values.length) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? { min: min - 1, max: max + 1 } : { min, max };
}

/** Maps a value in `domain` onto [lo, hi]. Callers pass (0, 100) for x and (100, 0) for y so
 *  the result is a plain percentage of the plot box (y inverted — 0% is the top).
 *  renderers-comparison.js:258. */
export function scaleFor(domain: CmpDomain, lo: number, hi: number): (v: number) => number {
  const span = domain.max - domain.min || 1;
  return (v: number) => lo + ((v - domain.min) / span) * (hi - lo);
}

/** `count` evenly-spaced tick values across the domain (inclusive of both ends).
 *  renderers-comparison.js:262. */
export function tickValues(domain: CmpDomain, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push(domain.min + ((domain.max - domain.min) * i) / (count - 1));
  return out;
}

/** Coarser than {@link formatMetricValue} — axis ticks want fewer digits as the value grows.
 *  renderers-comparison.js:267. */
export function tickText(v: number, outputType?: string): string {
  if (outputType === "percentage") return (v * 100).toFixed(0) + "%";
  if (Math.abs(v) >= 100) return v.toFixed(0);
  if (Math.abs(v) >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

// --- dot size -------------------------------------------------------------

// Real pixel diameters (renderers-comparison.js:234): scaled to 25% of an original size then up
// 50% twice, per repeated direct instruction. The size legend reuses these exact constants via
// dotDiameterPx so it resizes in lockstep with the real dots.
export const DOT_DIAMETER_MIN_PX = 5.625;
export const DOT_DIAMETER_MAX_PX = 23.625;
export const DOT_DIAMETER_FIXED_PX = 9;

/** A value's 0..1 position in the size domain, or null when there's no size metric / no value.
 *  renderers-comparison.js:276. */
export function sizeT(sizeDomain: CmpDomain | null, value: number | null | undefined): number | null {
  if (!sizeDomain || value == null) return null;
  const span = sizeDomain.max - sizeDomain.min || 1;
  return Math.max(0, Math.min(1, (value - sizeDomain.min) / span));
}

/** The dot's real pixel diameter — sqrt(t) so it's area-proportional, not diameter-proportional;
 *  the fixed size when there's no size metric or the player lacks the value.
 *  renderers-comparison.js:286. */
export function dotDiameterPx(sizeDomain: CmpDomain | null, value: number | null | undefined): number {
  const t = sizeT(sizeDomain, value);
  if (t == null) return DOT_DIAMETER_FIXED_PX;
  return DOT_DIAMETER_MIN_PX + (DOT_DIAMETER_MAX_PX - DOT_DIAMETER_MIN_PX) * Math.sqrt(t);
}

// --- colour ---------------------------------------------------------------

// ColorBrewer RdYlGn 3-stop (Cynthia Brewer): a direct 2-stop red→green blend desaturates to
// muddy grey at its midpoint, so a yellow mid-stop is the real fix. renderers-comparison.js:158.
type Rgb = readonly [number, number, number];
export const COLOR_LOW: Rgb = [215, 48, 39];
export const COLOR_MID: Rgb = [254, 224, 139];
export const COLOR_HIGH: Rgb = [26, 152, 80];

/** RdYlGn ramp: t in [0,1] → an rgb() string, via the yellow mid-stop. renderers-comparison.js:161. */
export function lerpColor(t: number): string {
  const c = Math.max(0, Math.min(1, t));
  const [from, to, localT] = c < 0.5 ? [COLOR_LOW, COLOR_MID, c / 0.5] : [COLOR_MID, COLOR_HIGH, (c - 0.5) / 0.5];
  const r = Math.round(from[0] + (to[0] - from[0]) * localT);
  const g = Math.round(from[1] + (to[1] - from[1]) * localT);
  const b = Math.round(from[2] + (to[2] - from[2]) * localT);
  return `rgb(${r}, ${g}, ${b})`;
}

// The 6 real position templates. Reuses 5 of this app's established comparison-mode hues plus
// one new teal for the 6th. renderers-comparison.js:177.
export const POSITION_COLORS: Record<string, string> = {
  goalkeeper: "#f5a623",
  full_back: "#4d8bf5",
  centre_back: "#b06df2",
  midfielder: "#2ecc71",
  attacking_midfielder: "#ff6b81",
  striker: "#2bb8b0",
};
export const DEFAULT_DOT_COLOR = "rgba(77, 139, 245, 0.85)";

/** A dot's colour: by position template (categorical), by a metric value on the RdYlGn ramp, or
 *  the flat default. renderers-comparison.js:291. */
export function pointColor(
  colorMode: CmpColorMode,
  colorDomain: CmpDomain | null,
  player: { positionTemplate?: string | null },
  value: number | null | undefined,
): string {
  if (colorMode === "position") return POSITION_COLORS[player.positionTemplate ?? ""] || DEFAULT_DOT_COLOR;
  if (colorMode && value != null && colorDomain) {
    const span = colorDomain.max - colorDomain.min || 1;
    return lerpColor((value - colorDomain.min) / span);
  }
  return DEFAULT_DOT_COLOR;
}

// --- magnetic hit-test ----------------------------------------------------

// Magnetic distance-based hover, not native :hover — a dense plot of small, potentially
// overlapping dots makes plain browser hit-testing unreliable (learned twice already on the
// Shot/Pass maps). renderers-comparison.js:437.
export const CMP_HOVER_PAD_PX = 6;
/** How far (px from its edge) the found player's dot catches the pointer. */
export const CMP_MAGNET_PX = 20;

/** Just the fields nearestPoint needs off a plotted point — its stored plot-box percentages and
 *  its diameter. The renderer's real point datum carries more (player, raw values); this stays
 *  minimal + generic so nearestPoint returns whatever the caller stored. */
export interface CmpPlottedPoint {
  leftPct: number;
  topPct: number;
  diameter: number;
}

/** A plot box's on-screen rectangle — the subset of DOMRect nearestPoint reads. The renderer
 *  passes `el.getBoundingClientRect()`; kept as a plain shape so the math is testable without DOM. */
export interface CmpRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The plotted point nearest the cursor, or null if none is within CMP_HOVER_PAD_PX of its edge.
 *  Each point's stored left%/top% is converted to a real on-screen centre against `rect`, then
 *  compared in real pixels (edge distance = centre distance minus the radius).
 *  renderers-comparison.js:438. */
export function nearestPoint<T extends CmpPlottedPoint>(
  rect: CmpRect,
  pointData: T[],
  clientX: number,
  clientY: number,
  magnetic?: (pd: T) => boolean,
): T | null {
  // The found player's dot pulls from further away (CMP_MAGNET_PX), and wins over its neighbours:
  // in a crowd it was hard to pick up (Ben, 2026-10-19).
  if (magnetic) {
    const pulled = pointData.filter(magnetic);
    const hit = nearestPointWithin(rect, pulled, clientX, clientY, CMP_MAGNET_PX);
    if (hit) return hit;
  }
  return nearestPointWithin(rect, pointData, clientX, clientY, CMP_HOVER_PAD_PX);
}

function nearestPointWithin<T extends CmpPlottedPoint>(
  rect: CmpRect,
  pointData: T[],
  clientX: number,
  clientY: number,
  pad: number,
): T | null {
  let best: T | null = null;
  let bestDist = Infinity;
  for (const pd of pointData) {
    const cx = rect.left + (pd.leftPct / 100) * rect.width;
    const cy = rect.top + (pd.topPct / 100) * rect.height;
    const d = Math.max(0, Math.hypot(clientX - cx, clientY - cy) - pd.diameter / 2);
    if (d < bestDist) {
      bestDist = d;
      best = pd;
    }
  }
  return best && bestDist <= pad ? best : null;
}
